// src/components/waypoint/DebriefScreen.tsx
import type { WaypointDebrief, WaypointSessionDetail } from "@/hooks/use-waypoint-sessions";

// ── Competency meta ───────────────────────────────────────────────────────────

const COMPETENCY_LABELS: Record<string, string> = {
  "consultative-partnership": "Consultative Partnership",
  "value-led-selling": "Value-Led Selling",
  "objection-navigation": "Objection Navigation",
  "stakeholder-orchestration": "Stakeholder Orchestration",
  "commercial-acumen": "Commercial Acumen",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function ratingLabel(r: number): string {
  if (r >= 4) return "Strong";
  if (r === 3) return "Solid";
  if (r === 2) return "Developing";
  return "Emerging";
}

function chipColor(r: number): { bg: string; color: string; border: string } {
  if (r >= 4)
    return { bg: "rgba(46,168,106,0.12)", color: "#2EA86A", border: "rgba(46,168,106,0.25)" };
  if (r === 3)
    return { bg: "rgba(64,126,193,0.12)", color: "#407EC1", border: "rgba(64,126,193,0.25)" };
  if (r === 2)
    return { bg: "rgba(232,169,60,0.12)", color: "#E8A93C", border: "rgba(232,169,60,0.25)" };
  return { bg: "rgba(216,68,60,0.10)", color: "#D8443C", border: "rgba(216,68,60,0.20)" };
}

function pipColor(filled: boolean, r: number): string {
  if (!filled) return "rgba(0,24,71,0.12)";
  if (r >= 4) return "#2EA86A";
  if (r === 3) return "#407EC1";
  if (r === 2) return "#E8A93C";
  return "#D8443C";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Pip score indicator ───────────────────────────────────────────────────────

function PipScore({ rating }: { rating: number }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
      {[1, 2, 3, 4, 5].map((pip) => (
        <span
          key={pip}
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: pipColor(pip <= rating, rating),
            border: pip <= rating ? "none" : "1px solid rgba(0,24,71,0.15)",
            display: "inline-block",
            transition: "background 0.2s",
          }}
        />
      ))}
    </div>
  );
}

// ── Competency row ────────────────────────────────────────────────────────────

function CompetencyRow({ slug, rating }: { slug: string; rating: number }) {
  const label = COMPETENCY_LABELS[slug] ?? slug;
  const { bg, color, border } = chipColor(rating);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "14px 0",
        borderBottom: "1px solid rgba(0,24,71,0.07)",
        gap: 16,
      }}
    >
      <p
        style={{
          flex: 1,
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 600,
          fontSize: 13.5,
          color: "#001847",
          margin: 0,
        }}
      >
        {label}
      </p>
      <PipScore rating={rating} />
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          padding: "3px 10px",
          borderRadius: 999,
          background: bg,
          color,
          border: `1px solid ${border}`,
          fontFamily: "'Montserrat', sans-serif",
          minWidth: 68,
          textAlign: "center" as const,
        }}
      >
        {ratingLabel(rating)}
      </span>
    </div>
  );
}

// ── Annotated turn ────────────────────────────────────────────────────────────

function AnnotatedTurn({
  sequence,
  competency,
  rating,
  rewrite,
}: {
  sequence: number;
  competency: string;
  rating: number;
  rewrite: string;
}) {
  const { bg, color, border } = chipColor(rating);
  const label = COMPETENCY_LABELS[competency] ?? competency;
  const isGap = rating <= 2;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e8edf5",
        borderRadius: 12,
        padding: "16px 20px",
        borderLeft: `3px solid ${color}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: 10,
            color: "#94a3b8",
            minWidth: 22,
          }}
        >
          #{sequence}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            padding: "3px 9px",
            borderRadius: 999,
            background: bg,
            color,
            border: `1px solid ${border}`,
            fontFamily: "'Montserrat', sans-serif",
            textTransform: "uppercase" as const,
            letterSpacing: "0.08em",
          }}
        >
          {label}
        </span>
        <PipScore rating={rating} />
      </div>

      {isGap && rewrite && (
        <div
          style={{
            background: "rgba(232,169,60,0.05)",
            border: "1px solid rgba(232,169,60,0.18)",
            borderRadius: 8,
            padding: "10px 14px",
          }}
        >
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase" as const,
              letterSpacing: "0.1em",
              color: "#E8A93C",
              margin: "0 0 5px",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            Better
          </p>
          <p
            style={{
              fontSize: 13,
              color: "#334155",
              lineHeight: 1.55,
              margin: 0,
              fontStyle: "italic",
            }}
          >
            "{rewrite}"
          </p>
        </div>
      )}
    </div>
  );
}

// ── Recommendation card ───────────────────────────────────────────────────────

function Recommendation({ rec }: { rec: WaypointDebrief["oneRecommendation"] }) {
  const label = COMPETENCY_LABELS[rec.competencySlug] ?? rec.competencySlug;

  const slices = [
    { pct: "70%", label: "Do this now", text: rec.actions["70"], color: "#2EA86A" },
    { pct: "20%", label: "Practice with peers", text: rec.actions["20"], color: "#407EC1" },
    { pct: "10%", label: "Study", text: rec.actions["10"], color: "#7FC2EC" },
  ];

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #001847 0%, #0E2658 100%)",
        borderRadius: 16,
        padding: "24px 28px",
        color: "#E6EEF8",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div>
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase" as const,
              letterSpacing: "0.14em",
              color: "#7FC2EC",
              margin: "0 0 4px",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            One thing to work on
          </p>
          <h3
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: 17,
              color: "#FAFBFD",
              margin: 0,
            }}
          >
            {label}
          </h3>
        </div>
        <span
          style={{
            fontSize: 12,
            color: "rgba(230,238,248,0.55)",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 600,
          }}
        >
          {rec.fromRating} → {rec.toRating}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {slices.map((s) => (
          <div key={s.pct} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: s.color,
                fontFamily: "'Montserrat', sans-serif",
                minWidth: 36,
                paddingTop: 1,
              }}
            >
              {s.pct}
            </span>
            <div>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.1em",
                  color: "rgba(230,238,248,0.45)",
                  margin: "0 0 3px",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                {s.label}
              </p>
              <p
                style={{
                  fontSize: 13,
                  color: "rgba(230,238,248,0.82)",
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {s.text}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Prosody section ───────────────────────────────────────────────────────────

function ProsodySection({ report }: { report: WaypointDebrief["prosodyReport"] }) {
  const { wpm, fillerCounts } = report;
  const paceOk = wpm >= 120 && wpm <= 180;
  const fillers = Object.entries(fillerCounts).sort(([, a], [, b]) => b - a);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #e8edf5",
        padding: "20px 24px",
      }}
    >
      <p
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase" as const,
          letterSpacing: "0.12em",
          color: "#64748b",
          margin: "0 0 16px",
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        Delivery
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" as const }}>
        <div>
          <p style={{ fontSize: 11, color: "#94a3b8", margin: "0 0 4px" }}>Speaking pace</p>
          <p
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 800,
              fontSize: 22,
              color: paceOk ? "#2EA86A" : "#E8A93C",
              margin: 0,
              lineHeight: 1,
            }}
          >
            {wpm}
            <span style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8", marginLeft: 4 }}>
              wpm
            </span>
          </p>
          <p style={{ fontSize: 11, color: "#94a3b8", margin: "4px 0 0" }}>
            {wpm > 180
              ? "A bit fast — try slowing down"
              : wpm < 120
                ? "A bit slow — try picking up pace"
                : "Good pace"}
          </p>
        </div>

        {fillers.length > 0 && (
          <div>
            <p style={{ fontSize: 11, color: "#94a3b8", margin: "0 0 8px" }}>Filler words</p>
            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
              {fillers.map(([word, count]) => (
                <span
                  key={word}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 9px",
                    borderRadius: 999,
                    background: "rgba(232,169,60,0.10)",
                    color: "#E8A93C",
                    border: "1px solid rgba(232,169,60,0.2)",
                  }}
                >
                  "{word}" × {count}
                </span>
              ))}
            </div>
          </div>
        )}
        {fillers.length === 0 && (
          <div>
            <p style={{ fontSize: 11, color: "#94a3b8", margin: "0 0 4px" }}>Filler words</p>
            <p style={{ fontSize: 13, color: "#2EA86A", fontWeight: 600, margin: 0 }}>
              None detected
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Scenario context ──────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  primary: "Primary",
  supporting: "Supporting",
  observer: "Observer",
};

const MOOD_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  cooperative: { bg: "rgba(46,168,106,0.10)", color: "#2EA86A", border: "rgba(46,168,106,0.22)" },
  neutral: { bg: "rgba(64,126,193,0.10)", color: "#407EC1", border: "rgba(64,126,193,0.22)" },
  adversarial: { bg: "rgba(216,68,60,0.10)", color: "#D8443C", border: "rgba(216,68,60,0.20)" },
};

function personaBlurb(behavior: string): string {
  const paras = behavior.split("\n\n").filter((p) => p.trim());
  // Strip "You are [Name], [Title]. " opener from first paragraph
  const first = (paras[0] ?? "").replace(/^You are [^.]+\.\s*/, "").trim();
  // Cap at ~180 chars for readability
  return first.length > 180 ? `${first.slice(0, 177)}…` : first;
}

function ScenarioContext({ session }: { session: WaypointSessionDetail }) {
  const scen = session.scenario;
  const seed = session.seed;
  if (!scen) return null;

  const brief = scen.briefVariants[seed.briefVariantIndex];

  return (
    <div style={{ marginBottom: 28 }}>
      <p
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase" as const,
          letterSpacing: "0.12em",
          color: "#64748b",
          margin: "0 0 12px",
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        The setup
      </p>

      {/* Brief card */}
      {brief && (
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #e8edf5",
            padding: "20px 24px",
            marginBottom: 12,
          }}
        >
          {/* Company / industry pill */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 999,
                background: "rgba(64,126,193,0.10)",
                color: "#407EC1",
                border: "1px solid rgba(64,126,193,0.22)",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              {brief.company}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "3px 10px",
                borderRadius: 999,
                background: "rgba(0,24,71,0.06)",
                color: "#334155",
                border: "1px solid rgba(0,24,71,0.10)",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              {brief.industry}
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column" as const, gap: 12 }}>
            {[
              { label: "Situation", text: brief.situation },
              { label: "Tension", text: brief.tension },
              { label: "Your objective", text: brief.objective },
            ].map(({ label, text }) => (
              <div key={label}>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    letterSpacing: "0.1em",
                    color: "#94a3b8",
                    margin: "0 0 3px",
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  {label}
                </p>
                <p style={{ fontSize: 13.5, color: "#1e293b", lineHeight: 1.55, margin: 0 }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Win conditions */}
      {scen.winConditions.length > 0 && (
        <div
          style={{
            background: "rgba(46,168,106,0.05)",
            borderRadius: 14,
            border: "1px solid rgba(46,168,106,0.18)",
            padding: "16px 20px",
            marginBottom: 12,
          }}
        >
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase" as const,
              letterSpacing: "0.1em",
              color: "#2EA86A",
              margin: "0 0 10px",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            Win conditions
          </p>
          <ul
            style={{
              margin: 0,
              padding: "0 0 0 16px",
              display: "flex",
              flexDirection: "column" as const,
              gap: 6,
            }}
          >
            {scen.winConditions.map((wc) => (
              <li key={wc} style={{ fontSize: 13.5, color: "#1e293b", lineHeight: 1.5 }}>
                {wc}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Persona roster */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e8edf5",
          padding: "18px 24px",
        }}
      >
        <p
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase" as const,
            letterSpacing: "0.1em",
            color: "#64748b",
            margin: "0 0 14px",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          Who was in the room
        </p>
        <div style={{ display: "flex", flexDirection: "column" as const, gap: 16 }}>
          {scen.roomRoles.map(({ role, personaSlug }, idx) => {
            const p = scen.personaMap[personaSlug];
            if (!p) return null;
            const mood = seed.personaMoods[personaSlug] ?? "neutral";
            const moodColors = MOOD_COLORS[mood] ?? MOOD_COLORS.neutral;
            const isLast = idx === scen.roomRoles.length - 1;

            return (
              <div
                key={personaSlug}
                style={{
                  display: "flex",
                  gap: 14,
                  paddingBottom: isLast ? 0 : 16,
                  borderBottom: isLast ? "none" : "1px solid rgba(0,24,71,0.06)",
                }}
              >
                {/* Avatar initial */}
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #001847, #1E6CC7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: "#fff",
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 800,
                    fontSize: 15,
                  }}
                >
                  {p.name.charAt(0)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Name + badges */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap" as const,
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#001847",
                      }}
                    >
                      {p.name}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "rgba(0,24,71,0.07)",
                        color: "#334155",
                        border: "1px solid rgba(0,24,71,0.10)",
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      {ROLE_LABELS[role] ?? role}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: moodColors.bg,
                        color: moodColors.color,
                        border: `1px solid ${moodColors.border}`,
                        fontFamily: "'Montserrat', sans-serif",
                        textTransform: "capitalize" as const,
                      }}
                    >
                      {mood}
                    </span>
                  </div>

                  {/* Title */}
                  <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 6px", fontWeight: 600 }}>
                    {p.title}
                  </p>

                  {/* Behavior blurb */}
                  <p style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.55, margin: 0 }}>
                    {personaBlurb(p.behavior)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

type Props = {
  session: WaypointSessionDetail;
  debrief: WaypointDebrief;
};

export function DebriefScreen({ session, debrief }: Props) {
  const scen = session.scenario;
  const scores = Object.entries(debrief.competencyScores).sort(
    ([, a], [, b]) => b.rating - a.rating,
  );
  const avgRating =
    scores.length > 0 ? scores.reduce((sum, [, s]) => sum + s.rating, 0) / scores.length : 0;
  const { bg: avgBg, color: avgColor, border: avgBorder } = chipColor(Math.round(avgRating));

  const DIFFICULTY_LABELS = {
    foundation: "Foundation",
    standard: "Standard",
    stretch: "Stretch",
  } as const;

  return (
    <div style={{ maxWidth: 840, animation: "wp-fade 0.3s ease-out", paddingBottom: 60 }}>
      {/* Navy gradient header */}
      <div
        style={{
          background: "linear-gradient(120deg, #001847 0%, #0E2658 60%, #16336E 100%)",
          borderRadius: 16,
          padding: "32px 36px",
          marginBottom: 32,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Subtle radial accent */}
        <div
          style={{
            position: "absolute",
            top: "-40%",
            right: "-10%",
            width: 320,
            height: 320,
            borderRadius: 999,
            background: "radial-gradient(circle, rgba(127,194,236,0.08), transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <p
          style={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase" as const,
            letterSpacing: "0.14em",
            color: "#7FC2EC",
            margin: "0 0 8px",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          Session debrief · {formatDate(session.createdAt)}
        </p>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap" as const,
            gap: 14,
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                fontSize: 28,
                color: "#FAFBFD",
                margin: "0 0 6px",
              }}
            >
              {scen?.name ?? "Waypoint"}
            </h1>
            <p style={{ fontSize: 13, color: "rgba(230,238,248,0.6)", margin: 0 }}>
              {DIFFICULTY_LABELS[session.difficulty]} difficulty
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ textAlign: "right" as const }}>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase" as const,
                  letterSpacing: "0.1em",
                  color: "rgba(230,238,248,0.45)",
                  margin: "0 0 2px",
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Overall
              </p>
              <span
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 800,
                  fontSize: 28,
                  color: avgColor,
                  lineHeight: 1,
                }}
              >
                {avgRating.toFixed(1)}
              </span>
              <span style={{ fontSize: 13, color: "rgba(230,238,248,0.4)", marginLeft: 2 }}>
                /5
              </span>
            </div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "5px 13px",
                borderRadius: 999,
                background: avgBg,
                color: avgColor,
                border: `1px solid ${avgBorder}`,
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              {ratingLabel(Math.round(avgRating))}
            </span>
          </div>
        </div>
      </div>

      {/* Scenario context */}
      <ScenarioContext session={session} />

      {/* Scorecard */}
      <div style={{ marginBottom: 28 }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase" as const,
            letterSpacing: "0.12em",
            color: "#64748b",
            margin: "0 0 4px",
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          Competency scorecard
        </p>
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #e8edf5",
            padding: "0 24px",
          }}
        >
          {scores.map(([slug, { rating }]) => (
            <CompetencyRow key={slug} slug={slug} rating={rating} />
          ))}
        </div>
      </div>

      {/* One recommendation */}
      <div style={{ marginBottom: 28 }}>
        <Recommendation rec={debrief.oneRecommendation} />
      </div>

      {/* Key moments */}
      {debrief.annotatedTurns.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase" as const,
              letterSpacing: "0.12em",
              color: "#64748b",
              margin: "0 0 12px",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            Key moments
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {debrief.annotatedTurns.map((turn) => (
              <AnnotatedTurn key={turn.sequence} {...turn} />
            ))}
          </div>
        </div>
      )}

      {/* What the room was thinking */}
      {Object.keys(debrief.seedReveal).length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase" as const,
              letterSpacing: "0.12em",
              color: "#64748b",
              margin: "0 0 12px",
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            What the room was thinking
          </p>
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              border: "1px solid #e8edf5",
              padding: "18px 24px",
              display: "flex",
              flexDirection: "column" as const,
              gap: 10,
            }}
          >
            {Object.entries(debrief.seedReveal).map(([key, value]) => (
              <div key={key} style={{ display: "flex", gap: 12 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#64748b",
                    textTransform: "capitalize" as const,
                    minWidth: 140,
                    flexShrink: 0,
                  }}
                >
                  {key.replace(/-/g, " ")}
                </span>
                <span style={{ fontSize: 13, color: "#001847" }}>{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delivery */}
      <ProsodySection report={debrief.prosodyReport} />
    </div>
  );
}
